import { createContext, ReactNode, useContext, useState, useEffect, useMemo } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { User } from "@shared/schema";
import { apiRequest, queryClient } from "../lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type RegistrationResult = {
  user: Omit<User, 'password'>;
  planId?: number;
  planName?: string;
  priceId?: string;
}

type AuthContextType = {
  user: Omit<User, 'password'> | null;
  isLoading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  loginMutation: UseMutationResult<Omit<User, 'password'>, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<RegistrationResult, Error, RegisterData>;
};

type LoginData = {
  username: string;
  password: string;
};

type RegisterData = {
  username: string;
  password: string;
  email: string;
  fullName: string;
  tenantName: string; // Company/organization name for tenant creation
  planId?: number; // Optional plan ID for subscription
  planName?: string; // Optional plan name for checkout
  priceId?: string; // Optional price ID for Stripe checkout
};

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const {
    data: user,
    error,
    isLoading,
    refetch
  } = useQuery<Omit<User, 'password'> | null, Error>({
    queryKey: ["/api/user"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/user", {
          credentials: 'include', // Include cookies for authentication
          cache: 'no-cache', // Prevent caching issues
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (res.status === 401) {
          // Clear cached authentication state when 401 received
          queryClient.setQueryData(["/api/user"], null);
          return null; // Not authenticated
        }
        if (!res.ok) {
          throw new Error(`${res.status}: ${res.statusText}`);
        }
        return res.json();
      } catch (error) {
        if (error instanceof Error && error.message?.includes('401')) {
          // Clear cached authentication state on auth errors
          queryClient.setQueryData(["/api/user"], null);
          return null; // Not authenticated
        }
        // For any other error, return null to prevent blank pages
        console.warn('Auth error:', error);
        return null;
      }
    },
    // Always confirm the current server session before making route decisions.
    staleTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: true, // Check auth when window regains focus
    refetchOnMount: true, // Always check auth on mount
    refetchOnReconnect: true, // Check auth when network reconnects
    gcTime: 5 * 60 * 1000, // 5 minutes
    // Retry failed requests less aggressively
    retry: 1,
    retryDelay: 1000,
    // Initial data to prevent any flicker on first load
    initialData: undefined,
    placeholderData: undefined,
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      const res = await apiRequest("/api/login", {
        method: "POST",
        body: credentials
      });
      return res;
    },
    onSuccess: (user: Omit<User, 'password'>) => {
      // Set the user data directly
      queryClient.setQueryData(["/api/user"], user);
      
      toast({
        title: "Login successful",
        description: `Welcome back, ${user.fullName}!`,
      });
      
      // Let the routing system handle navigation after initialization completes
    },
    onError: (error: Error) => {
      toast({
        title: "Login failed",
        description: error.message || "Invalid username or password",
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (credentials: RegisterData) => {
      // Extract plan information to be handled separately
      const { planId, planName, priceId, ...registerData } = credentials;
      
      const res = await apiRequest("/api/register", {
        method: "POST",
        body: {
          ...registerData,
          planId // Include planId in the registration request
        }
      });
      
      // Get planId from response if available, otherwise use the one from request
      const responsePlanId = res.planId || planId;
      
      // Return all the plan information for further processing
      return { 
        user: res, 
        planId: responsePlanId,
        planName: planName,
        priceId: priceId 
      };
    },
    onSuccess: (data: RegistrationResult) => {
      const { user } = data;
      
      // Set user data first to prevent authentication issues
      queryClient.setQueryData(["/api/user"], user);
      
      toast({
        title: "Registration successful",
        description: `Welcome to your 14-day free trial, ${user.fullName}!`,
      });
      
      // All new registrations are trial users, so redirect to dashboard
      setTimeout(() => {
        window.location.href = "/dashboard";
      }, 500);
    },
    onError: (error: Error) => {
      toast({
        title: "Registration failed",
        description: error.message || "Could not create account",
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("/api/logout", { method: "POST" });
    },
    onSuccess: () => {
      queryClient.setQueryData(["/api/user"], null);
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Create stable references to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user: user || null,
    isLoading,
    error,
    isAuthenticated: !!user,
    isSuperAdmin: !!(user?.isSuperAdmin),
    loginMutation,
    logoutMutation,
    registerMutation,
  }), [user, isLoading, error, loginMutation, logoutMutation, registerMutation]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    // During hot reloads, context might be temporarily unavailable
    // Return safe defaults instead of throwing to prevent blank pages
    console.warn("useAuth called outside AuthProvider, returning defaults");
    return {
      user: null,
      isLoading: true,
      error: null,
      isAuthenticated: false,
      isSuperAdmin: false,
      loginMutation: {} as any,
      logoutMutation: {} as any,
      registerMutation: {} as any,
    };
  }
  return context;
}
